import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { AuthProvider, useAuth } from '@/lib/auth';
import CustomerBooking from '@/pages/CustomerBooking';
import EventsPublic from '@/pages/EventsPublic';
import EmbedBooking from '@/pages/EmbedBooking';
import Confirmation from '@/pages/Confirmation';
import AdminLogin from '@/pages/AdminLogin';
import AuthCallback from '@/pages/AuthCallback';
import AdminLayout from '@/pages/AdminLayout';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminFloorPlan from '@/pages/AdminFloorPlan';
import AdminReservations from '@/pages/AdminReservations';
import AdminGuests from '@/pages/AdminGuests';
import AdminBlockedGuests from '@/pages/AdminBlockedGuests';
import AdminActivityLog from '@/pages/AdminActivityLog';
import AdminChecklist from '@/pages/AdminChecklist';
import AdminCalls from '@/pages/AdminCalls';
import AdminVouchers from '@/pages/AdminVouchers';
import AdminEvents from '@/pages/AdminEvents';
import AdminOffers from '@/pages/AdminOffers';
import AcceptOffer from '@/pages/AcceptOffer';
import AdminAreas from '@/pages/AdminAreas';
import AdminTables from '@/pages/AdminTables';
import AdminCombinations from '@/pages/AdminCombinations';
import AdminWaitlist from '@/pages/AdminWaitlist';
import AdminUsers from '@/pages/AdminUsers';
import AdminSettings from '@/pages/AdminSettings';
import AdminWeek from '@/pages/AdminWeek';
import AdminTags from '@/pages/AdminTags';
import AdminFeedback from '@/pages/AdminFeedback';
import SelfService from '@/pages/SelfService';
import FeedbackPage from '@/pages/FeedbackPage';
import Datenschutz from '@/pages/Datenschutz';
import Impressum from '@/pages/Impressum';
import TageskartePage from '@/pages/TageskartePage';
import BrandHead from '@/components/BrandHead';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

// Restricts a child route to specific roles. Service role can only access
// dashboard, floor-plan and waitlist; other admin pages redirect back to /admin.

function RoleGate({ allow, children }) {
  const { user } = useAuth();
  const role = user?.role;
  if (!role) return children;
  if (allow && !allow.includes(role)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth session_id during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<CustomerBooking />} />
      <Route path="/events" element={<EventsPublic />} />
      <Route path="/embed" element={<EmbedBooking />} />
      <Route path="/confirmation/:id" element={<Confirmation />} />
      <Route path="/r/:id" element={<SelfService />} />
      <Route path="/angebot/:token" element={<AcceptOffer />} />
      <Route path="/f/:token" element={<FeedbackPage />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/privacy" element={<Datenschutz />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/tageskarte" element={<TageskartePage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="week" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminWeek /></RoleGate>} />
        <Route path="floor-plan" element={<AdminFloorPlan />} />
        <Route path="reservations" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminReservations /></RoleGate>} />
        <Route path="guests" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminGuests /></RoleGate>} />
        <Route path="blocked-guests" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminBlockedGuests /></RoleGate>} />
        <Route path="calls" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminCalls /></RoleGate>} />
        <Route path="vouchers" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminVouchers /></RoleGate>} />
        <Route path="events" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminEvents /></RoleGate>} />
        <Route path="offers" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminOffers /></RoleGate>} />
        <Route path="areas" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminAreas /></RoleGate>} />
        <Route path="tables" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminTables /></RoleGate>} />
        <Route path="combinations" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminCombinations /></RoleGate>} />
        <Route path="tags" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminTags /></RoleGate>} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="checklist" element={<AdminChecklist />} />
        <Route path="feedback" element={<RoleGate allow={['super_admin','admin','restaurant_leader','manager']}><AdminFeedback /></RoleGate>} />
        <Route path="users" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminUsers /></RoleGate>} />
        <Route path="activity-log" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminActivityLog /></RoleGate>} />
        <Route path="settings" element={<RoleGate allow={['super_admin','admin','restaurant_leader']}><AdminSettings /></RoleGate>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BrandHead />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
